import React from 'react';
import './styles.scss';
import { Typography } from 'Components/atoms';
import { LogicBadge } from 'Components/atoms/LogicBadge';
import { ConditionRow } from 'Components/molecules/ConditionRow';
import { CondEntry, GroupNode } from 'Core-utils/types/rules.types';
import { emptyCond, emptyGroup } from 'Core-utils/helpers/ruleBuilderHelpers';
import { useI18n } from 'Hooks/useI18n';
import {
    RULE_BUILDER_GROUP_LOGIC_AND,
    RULE_BUILDER_GROUP_LOGIC_OR,
    RULE_BUILDER_GROUP_ADD_COND,
    RULE_BUILDER_GROUP_ADD_NESTED,
    RULE_BUILDER_GROUP_DELETE,
} from 'Core-utils/constants/constants';

export interface AdvGroupBoxProps {
    node: GroupNode;
    depth: number;
    onChange: (updated: GroupNode) => void;
    onDelete: () => void;
}

const AdvGroupBox = ({ node, depth, onChange, onDelete }: AdvGroupBoxProps) => {
    const { t } = useI18n();

    const toggleLogic = () => onChange({ ...node, logic: node.logic === 'and' ? 'or' : 'and' });

    const addCond = () =>
        onChange({ ...node, children: [...node.children, emptyCond()] });

    const addNestedGroup = () =>
        onChange({ ...node, children: [...node.children, emptyGroup()] });

    const updateChild = (id: string, updated: CondEntry | GroupNode) =>
        onChange({ ...node, children: node.children.map((c) => (c.id === id ? updated : c)) });

    const removeChild = (id: string) =>
        onChange({ ...node, children: node.children.filter((c) => c.id !== id) });

    const logicDesc =
        node.logic === 'and'
            ? t(RULE_BUILDER_GROUP_LOGIC_AND, 'All of the following must be true')
            : t(RULE_BUILDER_GROUP_LOGIC_OR, 'At least one of the following must be true');

    return (
        <div className={`rb-group rb-group--depth-${Math.min(depth, 3)}`}>
            <div className="rb-group__header">
                <span className="rb-group__drag" title="Drag to reorder">⠿⠿</span>
                <LogicBadge logic={node.logic} interactive onClick={toggleLogic} />
                <Typography variant="body5" className="rb-group__desc mb-0">
                    {logicDesc}
                </Typography>
                <div className="d-flex align-items-center gap-2 ms-auto">
                    <button
                        type="button"
                        className="rb-group__icon-btn"
                        title={t(RULE_BUILDER_GROUP_ADD_NESTED, 'Add nested group')}
                        onClick={addNestedGroup}
                    >
                        <span className="rb-group__icon-btn-plus">+</span>
                    </button>
                    <button
                        type="button"
                        className="rb-group__icon-btn rb-group__icon-btn--del"
                        title={t(RULE_BUILDER_GROUP_DELETE, 'Delete group')}
                        onClick={onDelete}
                    >
                        ×
                    </button>
                </div>
            </div>

            <div className="rb-group__body">
                {node.children.map((child) =>
                    child.kind === 'cond' ? (
                        <ConditionRow
                            key={child.id}
                            cond={child}
                            showDragHandle
                            onChange={(patch) => updateChild(child.id, { ...child, ...patch })}
                            onRemove={() => removeChild(child.id)}
                        />
                    ) : (
                        <AdvGroupBox
                            key={child.id}
                            node={child}
                            depth={depth + 1}
                            onChange={(updated) => updateChild(child.id, updated)}
                            onDelete={() => removeChild(child.id)}
                        />
                    ),
                )}
            </div>

            <button type="button" className="rb-group__add-cond" onClick={addCond}>
                {t(RULE_BUILDER_GROUP_ADD_COND, '+ Add condition')}
            </button>
        </div>
    );
};

export default AdvGroupBox;
